import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  Row,
  getFilteredRowModel,
  ColumnDef,
  SortingState,
  Column,
  ColumnResizeMode,
  Table,
  ColumnSizingState,
  CellContext,
  FilterFn,
} from '@tanstack/react-table';
import React, { useState, useRef, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { IconButton } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { OrderCleansingDto } from 'services/data-contracts';
import StatusIndicator from './StatusIndicator';
import DoneButton from 'components/input/DoneButton';
import { useTranslation } from 'react-i18next';
import i18n from 'i18n';
import { SettingItems } from 'types/CommonTypes';
import { getUrlParam, normalizeUrl } from 'libs/helper/utils';

/**
 * 서버 sort 파라미터 문자열을 React Table의 SortingState로 변환합니다.
 * 예: "orderId,asc" → [{ id: "orderId", desc: false }]
 *     "itemName,desc" → [{ id: "itemName", desc: true }]
 */
export function parseSortParam(sortParam?: string): SortingState {
  if (!sortParam) return [];
  const [id, order] = sortParam.split(',');
  if (!id || !order) return [];
  return [{ id, desc: order === 'desc' }];
}

const commonFilterFn: FilterFn<any> = (row, columnId, value) => {
  const cellValue = row.getValue(columnId);

  // undefined나 빈 문자열인 경우 필터링하지 않음
  if (!value) return true;

  // null 값 처리
  if (value === 'null') {
    if (columnId === 'status') return false;
    return !cellValue || cellValue === '' || cellValue === null || cellValue === undefined;
  }

  // 셀 값이 null이거나 undefined인 경우 빈 문자열로 처리
  const searchStr = (cellValue ?? '').toString().toLowerCase();
  const filterStr = value.toString().toLowerCase();

  // includes를 사용하여 부분 일치 검색
  return searchStr.includes(filterStr);
};

// 날짜를 YYYY.MM.DD HH:mm:ss 형식으로 포맷팅하는 함수
const formatDateTime = (date: string): string => {
  if (!date) return '';

  // UTC 문자열을 Date 객체로 변환
  const utcDate = new Date(date);

  // YYYY.MM.DD HH:mm:ss 형식으로 포맷팅
  const year = utcDate.getFullYear();
  const month = String(utcDate.getMonth() + 1).padStart(2, '0');
  const day = String(utcDate.getDate()).padStart(2, '0');
  const hours = String(utcDate.getHours()).padStart(2, '0');
  const minutes = String(utcDate.getMinutes()).padStart(2, '0');
  const seconds = String(utcDate.getSeconds()).padStart(2, '0');

  return `${year}.${month}.${day} ${hours}:${minutes}:${seconds}`;
};

// 2025-03-25 테이블 표시/비표시 기능 추가로 visibleColumnItems 및 항목 id추가
const createColumns = (
  t: (key: string) => string,
  visibleColumnItems: SettingItems[],
  handleRowClick?: (row: OrderCleansingDto) => void,
  handleDoneClick?: (row: OrderCleansingDto) => void,
): ColumnDef<OrderCleansingDto>[] => {
  const sortedVisibleItems = [...visibleColumnItems].sort((a, b) => (a.seq || 0) - (b.seq || 0));
  const baseTable: ColumnDef<OrderCleansingDto>[] = [
    {
      id: 'orderId',
      accessorKey: 'orderId',
      header: t('Order ID'),
      enableSorting: true,
      filterFn: commonFilterFn,

      cell: ({ getValue }: { getValue: () => any }) => (
        <div style={{ textAlign: 'center' }}>{String(getValue())}</div>
      ),
    },
    {
      id: 'trackingNo',
      accessorKey: 'trackingNo',
      header: t('Tracking No'),
      enableSorting: true,
      filterFn: commonFilterFn,
      cell: ({ getValue }: { getValue: () => any }) => (
        <div style={{ textAlign: 'center' }}>{getValue()}</div>
      ),
    },
    {
      id: 'itemName',
      accessorKey: 'itemName',
      header: t('Title(Origin)'),
      size: 300,
      cell: ({ row }: { row: Row<OrderCleansingDto> }) => (
        <TitleLink style={{ minWidth: 200 }} onClick={() => handleRowClick?.(row.original)}>
          {row.original.itemName}
        </TitleLink>
      ),
      filterFn: commonFilterFn,
    },
    {
      id: 'itemOption',
      accessorKey: 'itemOption',
      header: t('Option'),
      size: 200,
      cell: ({ getValue }: { getValue: () => any }) => (
        <div style={{ textAlign: 'center' }}>{getValue()}</div>
      ),
      filterFn: commonFilterFn,
    },
    {
      id: 'optionCode',
      accessorKey: 'optionCode',
      header: t('Option Code'),
      size: 200,
      cell: ({ getValue }: { getValue: () => any }) => (
        <div style={{ textAlign: 'center' }}>{getValue()}</div>
      ),
      filterFn: commonFilterFn,
    },
    {
      id: 'customsTitle',
      accessorKey: 'customsTitle',
      header: t('Cleansing Title'),
      size: 300,
      cell: ({ row }: { row: Row<OrderCleansingDto> }) => (
        <div style={{ textAlign: 'center' }}>
          {row.original.customsTitle === null
            ? row.original.recommendTitlePrimary
            : row.original.customsTitle}
        </div>
      ),
      filterFn: commonFilterFn,
    },
    {
      id: 'iconColumn',
      header: '',
      enableSorting: false,
      cell: ({ row }: { row: Row<OrderCleansingDto> }) => (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          {row.original.status === 'CLEANSED' && (
            <DoneButton onClick={() => handleDoneClick?.(row.original)} />
          )}
        </div>
      ),
      size: 100,
      enableColumnFilter: false,
    },
    {
      id: 'status',
      accessorKey: 'status',
      header: t('Status'),
      size: i18n.language === 'en' ? 200 : 120,
      minSize: i18n.language === 'en' ? 200 : 120,
      maxSize: i18n.language === 'en' ? 200 : 120,
      cell: ({ getValue }: { getValue: () => any }) => (
        <div style={{ width: '100%' }}>
          <StatusIndicator status={getValue() as OrderCleansingDto['status']} />
        </div>
      ),
      filterFn: commonFilterFn,
    },

    {
      id: 'hsCode',
      accessorKey: 'hsCode',
      header: t('HS Code'),
      filterFn: commonFilterFn,
    },
    {
      id: 'subMasterBl',
      accessorKey: 'subMasterBl',
      header: t('Sub Master BL'),
      filterFn: commonFilterFn,
    },
    {
      id: 'masterBl',
      accessorKey: 'masterBl',
      header: t('Master BL'),
      filterFn: commonFilterFn,
    },
    {
      id: 'itemCode',
      accessorKey: 'itemCode',
      header: t('Item Code'),
      filterFn: commonFilterFn,
    },
    {
      id: 'material',
      accessorKey: 'material',
      header: t('Material'),
      filterFn: commonFilterFn,
    },

    {
      id: 'itemUrl',
      accessorKey: 'itemUrl',
      header: t('Market'),
      cell: ({ row }: { row: Row<OrderCleansingDto> }) => {
        const url = normalizeUrl(row.original.itemUrl ?? '');
        const marketName = row.original.marketName;
        if (url === null) return <div>-</div>;

        return (
          <>
            <TitleLink as="a" href={url} target="_blank" rel="noopener noreferrer">
              {marketName ? `${marketName} ${t('visit')}` : t('visit site')}
            </TitleLink>
          </>
        );
      },
      filterFn: commonFilterFn,
    },
    {
      id: 'realWeight',
      accessorKey: 'realWeight',
      header: t('Weight'),
      filterFn: commonFilterFn,
      cell: ({ getValue }: { getValue: () => any }) => (
        <div style={{ textAlign: 'right' }}>{(getValue() as number)?.toLocaleString()}</div>
      ),
    },
    {
      id: 'unitPrice',
      accessorKey: 'unitPrice',
      header: t('Unit Price'),
      filterFn: commonFilterFn,
      cell: ({ getValue }: { getValue: () => any }) => (
        <div style={{ textAlign: 'center' }}>{(getValue() as number)?.toLocaleString()}</div>
      ),
    },
    {
      id: 'quantity',
      accessorKey: 'quantity',
      header: t('Quantity'),
      filterFn: commonFilterFn,
      cell: ({ getValue }: { getValue: () => any }) => (
        <div style={{ textAlign: 'center' }}>{(getValue() as number)?.toLocaleString()}</div>
      ),
    },
    {
      id: 'regDatetime',
      accessorKey: 'regDatetime',
      header: t('Created At'),
      size: 190,
      filterFn: (row, columnId, filterValue) => {
        const value = row.getValue(columnId) as string;
        return value >= filterValue[0] && value <= filterValue[1];
      },
      cell: (props: CellContext<OrderCleansingDto, unknown>) => {
        return formatDateTime(props.getValue() as string);
      },
    },
    {
      id: 'modifyDate',
      accessorKey: 'modifyDate',
      header: t('Modified At'),
      size: 190,
      cell: (props: CellContext<OrderCleansingDto, unknown>) => {
        return formatDateTime(props.getValue() as string);
      },
    },
  ];

  // seq 값으로 정렬된 컬럼만 필터링하여 반환
  return sortedVisibleItems
    .map((item) => ({
      column: baseTable.find((column) => column.id === item.id),
      visible: item.visible,
    }))
    .filter(({ column, visible }) => column !== undefined && visible)
    .map(({ column }) => column as ColumnDef<OrderCleansingDto>);
};

interface CommonTableProps {
  data: OrderCleansingDto[];
  handleRowClick?: (row: OrderCleansingDto) => void;
  handleDoneClick?: (row: OrderCleansingDto) => void;
  fixedColumns?: number;
  onSortingChange?: (sorting: SortingState) => void;
  externalSorting?: SortingState;
  visibleColumnItems: SettingItems[];
  pagination?: number;
}

const CommonTable = ({
  data,
  handleRowClick,
  handleDoneClick,
  fixedColumns = 0,
  onSortingChange,
  externalSorting,
  visibleColumnItems,
  pagination,
}: CommonTableProps) => {
  const { t } = useTranslation();
  const tableRef = useRef<HTMLDivElement>(null);
  const [sorting, setSorting] = useState<SortingState>(externalSorting || []);
  const [columnFilters, setColumnFilters] = useState([]);
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({
    id: 80,
    title: 400,
    cleansing_title: 200,
    action: 120,
    HSCode: 120,
    status: 100,
    createdAt: 150,
  });
  const [openFilterId, setOpenFilterId] = useState<string | null>(null);
  const [showScroll, setShowScroll] = useState(false);

  // column의 visible제어 로직 추가(visibleColumnItems가 바뀔때만 재렌더링)
  const columns: ColumnDef<OrderCleansingDto>[] = useMemo(
    () => createColumns(t, visibleColumnItems, handleRowClick, handleDoneClick),
    [t, visibleColumnItems],
  );

  const scrollToTop = (behavior: 'smooth' | 'instant' = 'smooth') => {
    if (tableRef.current) {
      tableRef.current.scrollTo({ top: 0, behavior });
    }
  };

  const handleSortingChange = (updatedSorting: SortingState) => {
    if (onSortingChange) {
      // 타입 안전성 확보
      const safeUpdatedSorting = Array.isArray(updatedSorting) ? updatedSorting : [];
      onSortingChange(safeUpdatedSorting);
    } else {
      // 내부 상태 업데이트
      setSorting(Array.isArray(updatedSorting) ? updatedSorting : []);
    }
  };

  const table = useReactTable({
    data,
    columns,
    defaultColumn: {
      minSize: 80,
      maxSize: 1000,
    },
    state: {
      sorting: externalSorting || sorting,
      columnFilters,
      columnSizing,
    },
    onSortingChange: handleSortingChange,
    onColumnFiltersChange: setColumnFilters,
    onColumnSizingChange: setColumnSizing,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    enableColumnResizing: true,
    columnResizeMode: 'onEnd' as ColumnResizeMode,
  });

  useEffect(() => {
    const sorted = getUrlParam('sort');
    if (!sorted) {
      table.setSorting([]);
    } else {
      table.setSorting(parseSortParam(sorted));
    }
  }, [location.search]);

  useEffect(() => {
    if (pagination) {
      scrollToTop('instant');
    }
  }, [pagination]);

  useEffect(() => {
    const checkScrollTop = () => {
      if (window.scrollY > 200) {
        setShowScroll(true);
      } else {
        setShowScroll(false);
      }
    };

    const handleScroll = () => {
      checkScrollTop();
      if (tableRef.current) {
        setShowScroll(tableRef.current.scrollTop > 100);
      }
    };

    window.addEventListener('scroll', checkScrollTop);
    tableRef.current?.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', checkScrollTop);
      tableRef.current?.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const Resizer = styled.div<{ isResizing: boolean }>`
    position: absolute;
    right: -2px;
    top: 0;
    height: 100%;
    width: 5px;
    cursor: col-resize;
    user-select: none;
    touch-action: none;
    background: transparent !important;

    &::before {
      content: '';
      position: absolute;
      right: 2px;
      width: 1px;
      height: 100%;
      border-right: 1px solid ${(props) => (props.isResizing ? '#2563eb' : 'rgba(0, 0, 0, 0.1)')};
      pointer-events: none;
    }
  `;

  const DropdownMenu = styled.div<{ isOpen: boolean; isFirst?: boolean }>`
    position: absolute;
    top: 100%;
    right: ${(props) => (props.isFirst ? 'unset' : '0')};
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 4px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    display: ${(props) => (props.isOpen ? 'block' : 'none')};
    z-index: 1000;
    width: 250px;
    max-height: 300px;
    overflow-y: auto;
  `;

  const MenuItem = styled.div`
    padding: 8px 16px;
    cursor: pointer;
    text-align: left;
    &:hover {
      background: #f5f5f5;
    }
  `;

  const TableHeader = ({
    column,
    table,
    isFixed,
  }: {
    column: Column<OrderCleansingDto>;
    table: Table<OrderCleansingDto>;
    isFixed?: boolean;
  }) => {
    const headerRef = useRef<HTMLTableCellElement>(null);
    const [isSticky, setIsSticky] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const isOpen = openFilterId === column.id;
    const [isResizing, setIsResizing] = useState(false);
    const isFirst = column.id === 'orderId';

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as Element;

        if (target.closest('.filter-icon-button') || target.closest('.dropdown-menu')) {
          return;
        }

        setOpenFilterId(null);
      };

      if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside);
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [isOpen, column.id]);

    const handleMouseDown = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsResizing(true);

      const startX = e.pageX;
      const startWidth = column.getSize();

      const handleMouseMove = (e: MouseEvent) => {
        const width = Math.max(startWidth + (e.pageX - startX), column.columnDef.minSize || 80);
        table.setColumnSizing((prev) => ({
          ...prev,
          [column.id]: width,
        }));
      };

      const handleMouseUp = () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        setIsResizing(false);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    };

    const handleClose = () => {
      setOpenFilterId(null);
    };

    const handleFilter = (value: string) => {
      if (value === t('All')) {
        column.setFilterValue(undefined);
      } else if (value === t('Empty') || value === '' || value === null || value === undefined) {
        column.setFilterValue('null');
      } else {
        column.setFilterValue(value);
      }
      handleClose();
    };

    const uniqueValues = new Set(
      table.getCoreRowModel().rows.map((row) => row.getValue(column.id)),
    );

    const menuItems = Array.from(uniqueValues).sort((a, b) => {
      if (a === null || a === undefined || a === '') return -1;
      if (b === null || b === undefined || b === '') return 1;
      return String(a).localeCompare(String(b));
    });

    const [filteredItems, setFilteredItems] = useState(menuItems);

    const getDisplayValue = (value: any) => {
      if (value === null || value === undefined || value === '') {
        return t('Empty');
      }
      if (column.id === 'status') {
        const statusMap: { [key: string]: string } = {
          RECEIVED: t('Preparing'),
          CLEANSED: t('Validation Pending'),
          COMPLETED: t('Completed'),
          PROCESSING: t('Processing'),
          VALIDATION_ERROR: t('Validation Error'),
          USER_VERIFIED: t('Verified'),
          TRANSMISSION_FAILED: t('Transmission Failed'),
        };
        return statusMap[String(value)] || String(value);
      }
      if (column.id === 'regDatetime' || column.id === 'modifyDate') {
        const date = new Date(value);
        return date.toLocaleString(undefined, {
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false,
        });
      }
      return String(value);
    };

    const handleSortClick = () => {
      if (column.getCanSort()) {
        // 정렬 로직 수정: 현재 정렬 상태에 따라 다음 상태로 순환
        const currentSortDirection = column.getIsSorted();

        if (currentSortDirection === false) {
          // 정렬되지 않은 상태 -> 오름차순
          table.setSorting([{ id: column.id, desc: false }]);
        } else if (currentSortDirection === 'asc') {
          // 오름차순 -> 내림차순
          table.setSorting([{ id: column.id, desc: true }]);
        } else {
          // 내림차순 -> 정렬 해제
          table.setSorting([]);
        }
      }
    };

    return (
      <th
        ref={headerRef}
        style={{
          position: 'relative',
          zIndex: 1,
          backgroundColor: 'white',
          width: `${column.getSize()}px`,
          padding: '12px 16px',
          borderBottom: '2px solid #e5e7eb',
          transition: 'background-color 0.2s, border-bottom-color 0.2s',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            overflow: 'hidden',
            gap: '4px',
          }}
        >
          <div
            onClick={handleSortClick}
            style={{
              cursor: column.getCanSort() ? 'pointer' : 'default',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flexGrow: 1,
              fontWeight:
                column.getFilterValue() !== undefined && column.getFilterValue() !== ''
                  ? '600'
                  : 'normal',
              color:
                column.getFilterValue() !== undefined && column.getFilterValue() !== ''
                  ? '#2563eb'
                  : 'inherit',
            }}
          >
            {column.getCanSort() && (
              <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                {column.getIsSorted() === 'asc' && <ArrowUpwardIcon sx={{ fontSize: 16 }} />}
                {column.getIsSorted() === 'desc' && <ArrowDownwardIcon sx={{ fontSize: 16 }} />}
              </span>
            )}
            {column.columnDef.header as string}
          </div>
          {/* 필터 아이콘 비활성화
          {column.getCanFilter() && (
            <div style={{ flexShrink: 0 }}>
              <IconButton
                className="filter-icon-button"
                size="small"
                onClick={handleClick}
                style={{
                  padding: '4px',
                  color:
                    column.getFilterValue() !== undefined && column.getFilterValue() !== ''
                      ? '#2563eb'
                      : 'rgba(0, 0, 0, 0.54)',
                }}
              >
                <FilterListIcon fontSize="small" />
              </IconButton>
            </div>
          )}
          */}
        </div>
        <Resizer onMouseDown={handleMouseDown} isResizing={isResizing} />
        <DropdownMenu className="dropdown-menu" isOpen={isOpen} isFirst={isFirst}>
          <MenuItem onClick={() => handleFilter(t('All'))}>{t('All')}</MenuItem>
          {filteredItems.map((value) => (
            <MenuItem
              key={`${column.id}-${String(value)}`}
              onClick={() => handleFilter(String(value))}
            >
              {getDisplayValue(value)}
            </MenuItem>
          ))}
        </DropdownMenu>
      </th>
    );
  };

  return (
    <div>
      <OuterTableWrapper>
        <InnerTableWrapper ref={tableRef}>
          <StyledTable>
            <thead
              style={{
                position: 'sticky',
                top: 0,
                zIndex: 3,
                backgroundColor: 'white',
                borderBottom: '1px solid #e5e7eb',
              }}
            >
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header, index) => (
                    <TableHeader
                      key={header.id}
                      column={header.column}
                      table={table}
                      isFixed={index < fixedColumns}
                    />
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell, index) => (
                    <TableCell key={cell.id} isFixed={index < fixedColumns}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </tbody>
          </StyledTable>
        </InnerTableWrapper>
      </OuterTableWrapper>

      <ScrollTopButtonWrapper showScroll={showScroll}>
        <IconButton
          onClick={() => scrollToTop('smooth')}
          style={{
            background: '#8d8d8d',
            zIndex: 1000,
            width: '60px',
            height: '60px',
          }}
        >
          <ArrowUpwardIcon style={{ color: '#fff' }} />
        </IconButton>
      </ScrollTopButtonWrapper>
    </div>
  );
};

const OuterTableWrapper = styled.div`
  width: 100%;
  /* overflow: hidden; */
  position: relative;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const InnerTableWrapper = styled.div`
  width: 100%;
  max-height: calc(100vh - 250px);
  min-height: calc(100vh - 250px);
  overflow-x: auto;
  overflow-y: auto;
  margin-right: -17px;
  margin-bottom: 20px;
  position: relative;

  thead {
    position: sticky;
    top: 0;
    z-index: 3;
    background-color: white;
    border-bottom: 1px solid #e5e7eb;
    transform: translateZ(0);
  }

  /* 스크롤바 스타일링 수정 */
  &::-webkit-scrollbar {
    height: 8px; /* 8px에서 2px로 변경 */
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }

  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;

const StyledTable = styled.table`
  padding-bottom: 100px;
  width: 100%;
  table-layout: fixed;
  border-collapse: separate;
  border-spacing: 0;
  background-color: white;
  border-top: 2px solid #2c2c2c;
  position: relative;
`;

const TableCell = styled.td<{ isFixed?: boolean }>`
  padding: 12px 16px;
  width: auto;
  min-width: auto;
  color: #374151;
  border-bottom: 1px solid #e5e7eb;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  position: relative;
  background-color: inherit;
  z-index: 0;

  tr:hover & {
    background-color: #f9fafb;
  }
`;

const TableRow = styled.tr`
  &:hover {
    td {
      background-color: #f9fafb;
    }
  }
`;

const TitleLink = styled.div`
  color: #3b82f6;
  cursor: pointer;

  &:hover {
    color: #1d4ed8;
  }
`;

const ScrollTopButtonWrapper = styled.div<{ showScroll: boolean }>`
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 100000;
  bottom: ${(props) => (props.showScroll ? '20px' : '-100px')};
  transition: bottom 0.3s ease-in-out;
`;

export default CommonTable;